// The reporting export (02 §2.1 "BigQuery export", 07 §7.4 "Personal data", 15 §15.1): the tables
// that go to BigQuery, their columns, and the pure functions that turn a Firestore document into a
// row. The rules of privacy live here, in one place:
//
//   * phone numbers are hashed (keyed, one-way), never exported;
//   * addresses, names, e-mail, free text (comments, notes, memos, subjects), photo and signature
//     paths, tokens, codes and staff identities are never exported;
//   * a row holds only what a report needs: ids, dates, amounts, counts, statuses.
//
// `docs/reports/README.md` lists every column. A test fails when a mapper emits a column that is not
// declared here, or a value that looks like a phone number.
//
// Each table is partitioned by DAY on `event_date` (the day the record last changed, or happened
// for records that never change). A changed record is exported again as a new version: its
// `insertId` is `{table}:{key}:{version}`, so a retry of the same version is dropped by BigQuery
// and a new version is a new row. Reports take the latest version of a key (see docs/reports).
export type BqType = 'STRING' | 'INTEGER' | 'FLOAT' | 'BOOLEAN' | 'TIMESTAMP' | 'DATE';

export interface BqColumn {
  name: string;
  type: BqType;
  /** REQUIRED columns are never null in an exported row. */
  required?: boolean;
  description: string;
}

export type ReportValue = string | number | boolean | null;
export type ReportRow = Record<string, ReportValue>;

export interface ReportContext {
  /** Keyed one-way hash of a phone number (E.164): the same number always gives the same hash. */
  hash: (e164: string) => string;
  exportedAt: string;
}

export const REPORTING_TABLE_NAMES = [
  'shipments',
  'boxes',
  'shipment_legs',
  'payments',
  'journals',
  'journal_lines',
  'feedback',
  'tickets',
  'partner_updates',
  'fx_rates',
] as const;
export type ReportingTableName = (typeof REPORTING_TABLE_NAMES)[number];

export interface ReportingTable {
  name: ReportingTableName;
  description: string;
  /** The Firestore collection the rows come from. */
  source: string;
  /** The field on the source documents the export pages by (ISO text), oldest first. */
  cursorField: string;
  /** Columns that identify the record; a new version of it has the same key. */
  keyColumns: readonly string[];
  /** Immutable records (journals, payments...) are exported once; the others on every change. */
  mutable: boolean;
  columns: readonly BqColumn[];
}

const REQUIRED_COMMON: BqColumn[] = [
  {
    name: 'event_date',
    type: 'DATE',
    required: true,
    description: 'Partition day (UTC): when the record last changed, or happened',
  },
  {
    name: 'exported_at',
    type: 'TIMESTAMP',
    required: true,
    description: 'When this version was exported',
  },
  {
    name: 'version_key',
    type: 'STRING',
    required: true,
    description: 'Distinguishes versions of one key; the latest is the greatest',
  },
];

const c = (name: string, type: BqType, description: string, required = false): BqColumn => ({
  name,
  type,
  description,
  ...(required ? { required } : {}),
});

const MILESTONES = [
  'collected',
  'paid',
  'at_origin_hub',
  'loaded',
  'departed',
  'arrived',
  'customs_released',
  'at_hub',
  'with_partner',
  'delivered',
] as const;

const LEG_COLUMNS = [
  'origin_dwell',
  'consolidation',
  'transit',
  'port_dwell',
  'customs',
  'devanning',
  'destination_dwell',
  'delivery',
] as const;

const LEG_FIELDS = [
  'originDwell',
  'consolidation',
  'transit',
  'portDwell',
  'customs',
  'devanning',
  'destinationDwell',
  'delivery',
] as const;

export const REPORTING_TABLES: readonly ReportingTable[] = [
  {
    name: 'shipments',
    description:
      'One row per version of a shipment (flattened). No names, phones, addresses or contents.',
    source: 'shipments',
    cursorField: 'updatedAt',
    keyColumns: ['tracking_no'],
    mutable: true,
    columns: [
      c('tracking_no', 'STRING', 'Airway bill number', true),
      ...REQUIRED_COMMON,
      c('updated_at', 'TIMESTAMP', 'Last change of the shipment'),
      c('version', 'INTEGER', 'Shipment document version'),
      c('origin_sector', 'STRING', 'Country that took it in'),
      c('origin_branch', 'STRING', 'Branch that took it in'),
      c('service', 'STRING', 'sea, air or prime'),
      c('source', 'STRING', 'counter, pickup, app, website...'),
      c('status', 'STRING', 'Derived shipment status'),
      c('destination_country', 'STRING', 'Destination country code'),
      c('destination_city_id', 'STRING', 'Destination city id'),
      c('destination_city', 'STRING', 'Destination city name'),
      c('destination_route_code', 'STRING', 'Route code of the city'),
      c('serving_branch_id', 'STRING', 'Destination hub that serves it'),
      c('delivery_mode', 'STRING', 'own or partner'),
      c('partner_id', 'STRING', 'Delivery partner, when handed to one'),
      c('box_count', 'INTEGER', 'Boxes (pieces) on the shipment'),
      c('total_weight_kg', 'FLOAT', 'Total weight'),
      c('declared_value_minor', 'INTEGER', 'Declared value, minor units'),
      c('currency', 'STRING', 'Currency of the amounts below'),
      c('gross_minor', 'INTEGER', 'Invoice total with VAT, minor units'),
      c('net_minor', 'INTEGER', 'Invoice total before VAT, minor units'),
      c('vat_minor', 'INTEGER', 'VAT, minor units'),
      c('paid_minor', 'INTEGER', 'Paid so far, minor units'),
      c('balance_minor', 'INTEGER', 'Still to pay, minor units'),
      c('payment_status', 'STRING', 'unpaid, partial, paid, overpaid...'),
      c('shipper_customer_id', 'STRING', 'Internal customer id of the shipper'),
      c('shipper_phone_hash', 'STRING', 'Keyed hash of the shipper phone'),
      c('consignee_phone_hash', 'STRING', 'Keyed hash of the first consignee phone'),
      c('season_tags', 'STRING', 'Season tag ids, comma separated'),
      c('collected_at', 'TIMESTAMP', 'When it was collected'),
      c('delivered_at', 'TIMESTAMP', 'When it was fully delivered'),
      c('created_at', 'TIMESTAMP', 'When the record was created'),
      c('on_hold', 'BOOLEAN', 'An active hold'),
      c('has_exception', 'BOOLEAN', 'Has an exception flag'),
      c('open_exception_count', 'INTEGER', 'Open exceptions'),
      c('predicted_from', 'DATE', 'Predicted delivery window, early end'),
      c('predicted_to', 'DATE', 'Predicted delivery window, late end'),
      c('predicted_basis', 'STRING', 'history or target'),
      ...MILESTONES.map((m) => c(`m_${m}_at`, 'TIMESTAMP', `When the ${m} milestone was reached`)),
    ],
  },
  {
    name: 'boxes',
    description:
      'One row per version of a box. No contents description, holds reasons or staff identities.',
    source: 'boxes',
    cursorField: 'updatedAt',
    keyColumns: ['barcode'],
    mutable: true,
    columns: [
      c('barcode', 'STRING', 'Box barcode', true),
      ...REQUIRED_COMMON,
      c('updated_at', 'TIMESTAMP', 'Last change of the box'),
      c('version', 'INTEGER', 'Box document version'),
      c('tracking_no', 'STRING', 'Shipment'),
      c('origin_sector', 'STRING', 'Country that took it in'),
      c('origin_branch', 'STRING', 'Branch that took it in'),
      c('kind', 'STRING', 'box or piece'),
      c('size_id', 'STRING', 'Box size code'),
      c('status', 'STRING', 'Box status'),
      c('location_kind', 'STRING', 'branch, trip, container, run, partner...'),
      c('location_id', 'STRING', 'Id of that place'),
      c('current_branch_id', 'STRING', 'Branch that last received it'),
      c('trip_id', 'STRING', 'Trip it is on'),
      c('container_id', 'STRING', 'Container it is in'),
      c('run_id', 'STRING', 'Delivery run'),
      c('partner_id', 'STRING', 'Partner holding it'),
      c('serving_branch_id', 'STRING', 'Destination hub that serves it'),
      c('destination_city_id', 'STRING', 'Destination city id'),
      c('delivery_mode', 'STRING', 'own or partner'),
      c('weight_kg', 'FLOAT', 'Weight'),
      c('damaged', 'BOOLEAN', 'Flagged damaged'),
      c('unaccounted', 'BOOLEAN', 'Flagged unaccounted for'),
      c('returning', 'BOOLEAN', 'Being returned'),
      c('abandoned', 'BOOLEAN', 'Abandoned'),
      c('has_active_hold', 'BOOLEAN', 'An active hold'),
      c('last_event_type', 'STRING', 'Type of its last scan'),
      c('last_event_at', 'TIMESTAMP', 'Time of its last scan'),
    ],
  },
  {
    name: 'shipment_legs',
    description: "One row per version of a shipment's leg times (days) and on-time flags.",
    source: 'shipmentLegs',
    cursorField: 'computedAt',
    keyColumns: ['tracking_no'],
    mutable: true,
    columns: [
      c('tracking_no', 'STRING', 'Airway bill number', true),
      ...REQUIRED_COMMON,
      c('computed_at', 'TIMESTAMP', 'When the legs were worked out'),
      c('route', 'STRING', 'Origin-route code, e.g. KSA-MNL'),
      c('service', 'STRING', 'sea, air or prime'),
      c('origin_sector', 'STRING', 'Country that took it in'),
      c('origin_branch', 'STRING', 'Branch that took it in'),
      c('destination_sector', 'STRING', 'Destination country'),
      c('serving_branch_id', 'STRING', 'Destination hub that serves it'),
      c('city_id', 'STRING', 'Destination city id'),
      c('city', 'STRING', 'Destination city'),
      c('region', 'STRING', 'Destination region'),
      c('partner_id', 'STRING', 'Delivery partner'),
      c('delivery_mode', 'STRING', 'own or partner'),
      c('box_count', 'INTEGER', 'Boxes'),
      c('container_count', 'INTEGER', 'Containers it travelled in'),
      c(
        'container_ids',
        'STRING',
        'Container ids, comma separated (to share a shipment over its containers)',
      ),
      c('collected_at', 'TIMESTAMP', 'Collected'),
      c('delivered_at', 'TIMESTAMP', 'Delivered'),
      ...LEG_COLUMNS.map((l) => c(`${l}_days`, 'FLOAT', `Days in the ${l.replace(/_/g, ' ')} leg`)),
      c('door_to_door_days', 'FLOAT', 'Collected to delivered, days'),
      c('target_days', 'FLOAT', 'Door-to-door target in force'),
      c('target_source', 'STRING', 'sla or default'),
      c('last_mile_target_days', 'FLOAT', 'City delivery target, days'),
      c('on_time', 'BOOLEAN', 'Door to door within target'),
      c('last_mile_on_time', 'BOOLEAN', 'Last mile within the city target'),
      c('complete', 'BOOLEAN', 'Delivered with every leg known'),
    ],
  },
  {
    name: 'payments',
    description: 'One row per payment. No references, receipt text or staff identities.',
    source: 'payments',
    cursorField: 'at',
    keyColumns: ['payment_id'],
    mutable: false,
    columns: [
      c('payment_id', 'STRING', 'Payment id', true),
      ...REQUIRED_COMMON,
      c('at', 'TIMESTAMP', 'When the money was received or paid'),
      c('receipt_no', 'STRING', 'Receipt number'),
      c('direction', 'STRING', 'in or out'),
      c('method', 'STRING', 'cash, card, bank, cheque, wallet'),
      c('amount_minor', 'INTEGER', 'Amount, minor units'),
      c('currency', 'STRING', 'Currency'),
      c('to_deposit_minor', 'INTEGER', 'Part kept as customer credit'),
      c('shipment_count', 'INTEGER', 'Shipments it was applied to'),
      c('first_shipment_id', 'STRING', 'First shipment it was applied to'),
      c('allocated_minor', 'INTEGER', 'Part applied to shipments'),
      c('customer_id', 'STRING', 'Internal customer id'),
      c('branch_id', 'STRING', 'Branch'),
      c('sector_id', 'STRING', 'Country'),
      c('by_driver', 'BOOLEAN', 'Taken by a driver at the door'),
    ],
  },
  {
    name: 'journals',
    description: 'One row per journal (header). No memo and no staff identities.',
    source: 'journals',
    cursorField: 'postedAt',
    keyColumns: ['journal_id'],
    mutable: false,
    columns: [
      c('journal_id', 'STRING', 'Journal id: entity__source__id__vN', true),
      ...REQUIRED_COMMON,
      c('posted_at', 'TIMESTAMP', 'When it was posted'),
      c('entity_id', 'STRING', 'Country with its own books'),
      c('branch_id', 'STRING', 'Branch'),
      c('currency', 'STRING', 'Currency of the lines'),
      c('business_date', 'DATE', 'Business date in the entity'),
      c('period', 'STRING', 'Accounting period YYYY-MM'),
      c('source_type', 'STRING', 'What posted it'),
      c('source_id', 'STRING', 'The record it came from'),
      c('journal_version', 'INTEGER', 'Version of the same source'),
      c('status', 'STRING', 'posted...'),
      c('journal_no', 'INTEGER', 'Gap-free number, when assigned'),
      c('line_count', 'INTEGER', 'Lines'),
      c('total_debit_minor', 'INTEGER', 'Sum of debits'),
      c('total_credit_minor', 'INTEGER', 'Sum of credits'),
    ],
  },
  {
    name: 'journal_lines',
    description:
      'One row per journal line with its dimensions (shipment, container, campaign...). No driver, till or bank identities.',
    source: 'journals',
    cursorField: 'postedAt',
    keyColumns: ['journal_id', 'line_no'],
    mutable: false,
    columns: [
      c('journal_id', 'STRING', 'Journal id', true),
      c('line_no', 'INTEGER', 'Line number from 1', true),
      ...REQUIRED_COMMON,
      c('posted_at', 'TIMESTAMP', 'When it was posted'),
      c('entity_id', 'STRING', 'Country with its own books'),
      c('branch_id', 'STRING', 'Branch of the line, else of the journal'),
      c('currency', 'STRING', 'Currency'),
      c('business_date', 'DATE', 'Business date in the entity'),
      c('period', 'STRING', 'Accounting period YYYY-MM'),
      c('source_type', 'STRING', 'What posted it'),
      c('source_id', 'STRING', 'The record it came from'),
      c('account_code', 'STRING', 'Chart of accounts code'),
      c('debit_minor', 'INTEGER', 'Debit, minor units'),
      c('credit_minor', 'INTEGER', 'Credit, minor units'),
      c('shipment_id', 'STRING', 'Dimension: shipment'),
      c('customer_id', 'STRING', 'Dimension: internal customer id'),
      c('container_id', 'STRING', 'Dimension: container'),
      c('trip_id', 'STRING', 'Dimension: trip'),
      c('campaign_id', 'STRING', 'Dimension: campaign'),
      c('supplier_id', 'STRING', 'Dimension: supplier'),
      c('agent_id', 'STRING', 'Dimension: collection agent'),
      c('counter_entity_id', 'STRING', 'Dimension: the other country of an intercompany line'),
      c('wallet_id', 'STRING', 'Dimension: wallet'),
    ],
  },
  {
    name: 'feedback',
    description:
      'One row per rating. The comment, photos, names and phone numbers are not exported.',
    source: 'feedback',
    cursorField: 'createdAt',
    keyColumns: ['feedback_id'],
    mutable: false,
    columns: [
      c('feedback_id', 'STRING', 'Rating id', true),
      ...REQUIRED_COMMON,
      c('created_at', 'TIMESTAMP', 'When it was given'),
      c('kind', 'STRING', 'service or delivery'),
      c('stars', 'INTEGER', '1 to 5'),
      c('source', 'STRING', 'whatsapp, app...'),
      c('role', 'STRING', 'shipper, consignee or shared'),
      c('tracking_no', 'STRING', 'Shipment'),
      c('customer_id', 'STRING', 'Internal customer id'),
      c('branch_id', 'STRING', 'Branch that took the shipment in'),
      c('sector_id', 'STRING', 'Country'),
      c('serving_branch_id', 'STRING', 'Destination branch that served it'),
      c('route_code', 'STRING', 'Route'),
      c('partner_id', 'STRING', 'Partner'),
      c('complaint', 'BOOLEAN', 'A complaint was recognised'),
      c('has_comment', 'BOOLEAN', 'The customer wrote a comment'),
      c('comment_length', 'INTEGER', 'Length of the comment, characters'),
      c('has_ticket', 'BOOLEAN', 'A ticket was opened'),
      c('publishable', 'BOOLEAN', 'Agreed to be published'),
    ],
  },
  {
    name: 'tickets',
    description:
      'One row per version of a customer case. No names, phones, subject, messages or notes.',
    source: 'tickets',
    cursorField: 'updatedAt',
    keyColumns: ['ticket_id'],
    mutable: true,
    columns: [
      c('ticket_id', 'STRING', 'Ticket id', true),
      ...REQUIRED_COMMON,
      c('created_at', 'TIMESTAMP', 'Opened'),
      c('updated_at', 'TIMESTAMP', 'Last change'),
      c('category', 'STRING', 'delay, damage, missing_item...'),
      c('status', 'STRING', 'open, waiting, resolved, closed'),
      c('source', 'STRING', 'Where it came from'),
      c('rating', 'INTEGER', 'The rating that opened it'),
      c('tracking_no', 'STRING', 'Shipment'),
      c('customer_id', 'STRING', 'Internal customer id'),
      c('branch_id', 'STRING', 'Branch of customer care'),
      c('sector_id', 'STRING', 'Country'),
      c('sla_due_at', 'TIMESTAMP', 'SLA deadline'),
      c('message_count', 'INTEGER', 'Messages on the ticket'),
      c('resolution_action', 'STRING', 'apology, voucher, claim...'),
      c('resolved_at', 'TIMESTAMP', 'Resolved'),
      c('followup_resolved', 'BOOLEAN', 'The customer said it was solved'),
    ],
  },
  {
    name: 'partner_updates',
    description:
      'One row per partner status update. No receiver name, note, original text or proof paths.',
    source: 'partnerUpdates',
    cursorField: 'createdAt',
    keyColumns: ['update_id'],
    mutable: false,
    columns: [
      c('update_id', 'STRING', 'Update id', true),
      ...REQUIRED_COMMON,
      c('at', 'TIMESTAMP', 'When it happened'),
      c('created_at', 'TIMESTAMP', 'When it was recorded'),
      c('partner_id', 'STRING', 'Partner'),
      c('handover_id', 'STRING', 'Handover'),
      c('unit_count', 'INTEGER', 'Boxes the update names'),
      c('status', 'STRING', 'delivered, failed_attempt...'),
      c('source', 'STRING', 'link, staff, import, adapter...'),
      c('reason', 'STRING', 'Failure reason'),
      c('delivery_verified', 'BOOLEAN', 'A delivery came with the right code'),
      c('collected_minor', 'INTEGER', 'Cash collected for us, minor units'),
      c('collected_currency', 'STRING', 'Currency of that cash'),
      c('refused_count', 'INTEGER', 'Units the server refused'),
      c('branch_id', 'STRING', 'Branch'),
      c('sector_id', 'STRING', 'Country'),
    ],
  },
  {
    name: 'fx_rates',
    description:
      'Dated exchange rates (1 unit of from_currency in to_currency), to convert amounts in reports.',
    source: 'fxRates',
    cursorField: 'date',
    keyColumns: ['fx_id'],
    mutable: false,
    columns: [
      c('fx_id', 'STRING', 'Rate id: date__from__to', true),
      ...REQUIRED_COMMON,
      c('rate_date', 'DATE', 'The day the rate is for'),
      c('from_currency', 'STRING', 'Currency converted from'),
      c('to_currency', 'STRING', 'Currency converted to'),
      c('rate', 'FLOAT', '1 unit of from_currency in to_currency'),
    ],
  },
];

const TABLE_BY_NAME = new Map(REPORTING_TABLES.map((t) => [t.name, t]));

export function reportingTable(name: ReportingTableName): ReportingTable {
  return TABLE_BY_NAME.get(name) as ReportingTable;
}

// ----------------------------------------------------------------------- helpers

type Doc = Record<string, unknown>;

function at(doc: unknown, path: string): unknown {
  let node: unknown = doc;
  for (const key of path.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = (node as Doc)[key];
  }
  return node;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const int = (v: unknown): number | null => {
  const n = num(v);
  return n === null ? null : Math.trunc(n);
};
const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);

/** An ISO text if it reads as a time, else null. */
function time(v: unknown): string | null {
  const s = str(v);
  return s !== null && Number.isFinite(Date.parse(s)) ? s : null;
}

/** The UTC date of a time, for partitioning. */
export function dayOf(iso: string): string {
  return new Date(Date.parse(iso)).toISOString().slice(0, 10);
}

const dateOnly = (v: unknown): string | null => {
  const s = str(v);
  return s !== null && /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : null;
};

/** Common columns. A record with no usable time is not exported (null). */
function common(when: string | null, ctx: ReportContext, versionKey?: string): ReportRow | null {
  if (when === null) return null;
  return {
    event_date: dayOf(when),
    exported_at: ctx.exportedAt,
    version_key: versionKey ?? '1',
  };
}

function phoneHash(ctx: ReportContext, phone: unknown): string | null {
  const p = str(phone);
  return p === null ? null : ctx.hash(p);
}

/** Boxes the update names or the ticket carries: a count, never the list. */
const countOf = (v: unknown): number => (Array.isArray(v) ? v.length : 0);

// --------------------------------------------------------------------- mappers

export function shipmentRow(id: string, d: Doc, ctx: ReportContext): ReportRow | null {
  const updated = time(d['updatedAt']) ?? time(d['createdAt']) ?? time(d['collectedAt']);
  const base = common(updated, ctx, updated ?? '');
  if (!base) return null;
  const milestones = Array.isArray(d['milestones']) ? (d['milestones'] as Doc[]) : [];
  const m = Object.fromEntries(
    MILESTONES.map((key) => [
      `m_${key}_at`,
      time(milestones.find((x) => x['key'] === key)?.['at']),
    ]),
  );
  const consigneePhones = at(d, 'consignee.phones');
  return {
    tracking_no: id,
    ...base,
    updated_at: updated,
    version: int(d['version']),
    origin_sector: str(d['sectorId']),
    origin_branch: str(d['branchId']),
    service: str(d['service']),
    source: str(d['source']),
    status: str(d['status']),
    destination_country: str(at(d, 'destination.country')),
    destination_city_id: str(at(d, 'destination.cityId')),
    destination_city: str(at(d, 'consignee.cityName')),
    destination_route_code: str(at(d, 'destination.routeCode')),
    serving_branch_id: str(at(d, 'destination.servingBranchId')),
    delivery_mode: str(at(d, 'destination.mode')),
    partner_id: str(at(d, 'delivery.partnerId')) ?? str(at(d, 'destination.partnerId')),
    box_count: int(d['boxCount']),
    total_weight_kg: num(d['totalWeightKg']),
    declared_value_minor: int(d['declaredValueMinor']),
    currency: str(at(d, 'invoice.currency')),
    gross_minor: int(at(d, 'invoice.grossMinor')),
    net_minor: int(at(d, 'invoice.netMinor')),
    vat_minor: int(at(d, 'invoice.vatMinor')),
    paid_minor: int(at(d, 'money.paidMinor')),
    balance_minor: int(at(d, 'money.balanceMinor')),
    payment_status: str(at(d, 'money.status')),
    shipper_customer_id: str(at(d, 'shipper.customerId')),
    shipper_phone_hash: phoneHash(ctx, at(d, 'shipper.phoneE164')),
    consignee_phone_hash: phoneHash(
      ctx,
      Array.isArray(consigneePhones) ? consigneePhones[0] : null,
    ),
    season_tags: Array.isArray(d['seasonTags'])
      ? (d['seasonTags'] as unknown[]).filter((t) => typeof t === 'string').join(',')
      : null,
    collected_at: time(d['collectedAt']),
    delivered_at: time(d['deliveredAt']),
    created_at: time(d['createdAt']),
    on_hold: bool(at(d, 'flags.onHold')),
    has_exception: bool(at(d, 'flags.hasException')),
    open_exception_count: int(d['openExceptionCount']),
    predicted_from: dateOnly(at(d, 'predictedWindow.from')),
    predicted_to: dateOnly(at(d, 'predictedWindow.to')),
    predicted_basis: str(at(d, 'predictedWindow.basis')),
    ...m,
  } as ReportRow;
}

export function boxRow(id: string, d: Doc, ctx: ReportContext): ReportRow | null {
  const updated = time(d['updatedAt']);
  const base = common(updated, ctx, updated ?? '');
  if (!base) return null;
  const holds = Array.isArray(d['holds']) ? (d['holds'] as Doc[]) : [];
  return {
    barcode: id,
    ...base,
    updated_at: updated,
    version: int(d['version']),
    tracking_no: str(d['trackingNo']) ?? str(d['shipmentId']),
    origin_sector: str(d['sectorId']),
    origin_branch: str(d['branchId']),
    kind: str(d['kind']),
    size_id: str(d['sizeId']),
    status: str(d['status']),
    location_kind: str(at(d, 'location.kind')),
    location_id: str(at(d, 'location.id')),
    current_branch_id: str(d['currentBranchId']),
    trip_id: str(d['tripId']),
    container_id: str(d['containerId']),
    run_id: str(d['runId']),
    partner_id: str(at(d, 'partner.partnerId')) ?? str(at(d, 'destination.partnerId')),
    serving_branch_id: str(d['servingBranchId']),
    destination_city_id: str(at(d, 'destination.cityId')),
    delivery_mode: str(at(d, 'destination.mode')),
    weight_kg: num(d['weightKg']),
    damaged: bool(at(d, 'flags.damaged')),
    unaccounted: bool(at(d, 'flags.unaccounted')),
    returning: bool(at(d, 'flags.returning')),
    abandoned: bool(at(d, 'flags.abandoned')),
    has_active_hold: holds.some((h) => h['releasedAt'] === undefined),
    last_event_type: str(at(d, 'lastEvent.type')),
    last_event_at: time(at(d, 'lastEvent.at')),
  } as ReportRow;
}

export function legsRow(id: string, d: Doc, ctx: ReportContext): ReportRow | null {
  const computed = time(d['computedAt']);
  const base = common(computed, ctx, computed ?? '');
  if (!base) return null;
  const legs = Object.fromEntries(
    LEG_FIELDS.map((field, i) => [`${LEG_COLUMNS[i]}_days`, num(at(d, `legs.${field}`))]),
  );
  return {
    tracking_no: id,
    ...base,
    computed_at: computed,
    route: str(d['route']),
    service: str(d['service']),
    origin_sector: str(d['sectorId']),
    origin_branch: str(d['branchId']),
    destination_sector: str(d['destinationSectorId']),
    serving_branch_id: str(d['servingBranchId']),
    city_id: str(d['cityId']),
    city: str(d['cityName']),
    region: str(d['region']),
    partner_id: str(d['partnerId']),
    delivery_mode: str(d['deliveryMode']),
    box_count: int(d['boxCount']),
    container_count: countOf(d['containerIds']),
    container_ids: Array.isArray(d['containerIds'])
      ? (d['containerIds'] as unknown[]).filter((x) => typeof x === 'string').join(',')
      : null,
    collected_at: time(d['collectedAt']),
    delivered_at: time(d['deliveredAt']),
    ...legs,
    door_to_door_days: num(d['doorToDoorDays']),
    target_days: num(d['targetDays']),
    target_source: str(d['targetSource']),
    last_mile_target_days: num(d['lastMileTargetDays']),
    on_time: bool(d['onTime']),
    last_mile_on_time: bool(d['lastMileOnTime']),
    complete: bool(d['complete']),
  } as ReportRow;
}

export function paymentRow(id: string, d: Doc, ctx: ReportContext): ReportRow | null {
  const when = time(d['at']);
  const base = common(when, ctx);
  if (!base || !when) return null;
  const allocations = Array.isArray(d['allocations']) ? (d['allocations'] as Doc[]) : [];
  const shipmentIds = Array.isArray(d['shipmentIds']) ? (d['shipmentIds'] as unknown[]) : [];
  return {
    payment_id: id,
    ...base,
    at: when,
    receipt_no: str(d['receiptNo']),
    direction: str(d['direction']),
    method: str(d['method']),
    amount_minor: int(d['amountMinor']),
    currency: str(d['currency']),
    to_deposit_minor: int(d['toDepositMinor']),
    shipment_count: shipmentIds.length,
    first_shipment_id: str(shipmentIds[0]),
    allocated_minor: allocations.reduce((s, a) => s + (int(a['amountMinor']) ?? 0), 0),
    customer_id: str(d['customerId']),
    branch_id: str(d['branchId']),
    sector_id: str(d['sectorId']),
    by_driver: str(d['driverUid']) !== null,
  } as ReportRow;
}

interface StoredLine {
  accountCode?: unknown;
  debitMinor?: unknown;
  creditMinor?: unknown;
  dimensions?: unknown;
}

function journalLines(d: Doc): StoredLine[] {
  return Array.isArray(d['lines']) ? (d['lines'] as StoredLine[]) : [];
}

export function journalRow(id: string, d: Doc, ctx: ReportContext): ReportRow | null {
  const posted = time(d['postedAt']);
  const base = common(posted, ctx);
  if (!base) return null;
  const lines = journalLines(d);
  return {
    journal_id: id,
    ...base,
    posted_at: posted,
    entity_id: str(d['entityId']),
    branch_id: str(d['branchId']),
    currency: str(d['currency']),
    business_date: dateOnly(d['businessDate']),
    period: str(d['period']),
    source_type: str(d['sourceType']),
    source_id: str(d['sourceId']),
    journal_version: int(d['version']),
    status: str(d['status']),
    journal_no: int(d['journalNo']),
    line_count: lines.length,
    total_debit_minor: lines.reduce((s, l) => s + (int(l.debitMinor) ?? 0), 0),
    total_credit_minor: lines.reduce((s, l) => s + (int(l.creditMinor) ?? 0), 0),
  } as ReportRow;
}

/** One row per line of a journal. */
export function journalLineRows(id: string, d: Doc, ctx: ReportContext): ReportRow[] {
  const posted = time(d['postedAt']);
  const base = common(posted, ctx);
  if (!base) return [];
  return journalLines(d).map((line, i) => {
    const dim = (key: string): string | null => str(at(line.dimensions, key));
    return {
      journal_id: id,
      line_no: i + 1,
      ...base,
      posted_at: posted,
      entity_id: str(d['entityId']),
      branch_id: dim('branchId') ?? str(d['branchId']),
      currency: str(d['currency']),
      business_date: dateOnly(d['businessDate']),
      period: str(d['period']),
      source_type: str(d['sourceType']),
      source_id: str(d['sourceId']),
      account_code: str(line.accountCode),
      debit_minor: int(line.debitMinor),
      credit_minor: int(line.creditMinor),
      shipment_id: dim('shipmentId'),
      customer_id: dim('customerId'),
      container_id: dim('containerId'),
      trip_id: dim('tripId'),
      campaign_id: dim('campaignId'),
      supplier_id: dim('supplierId'),
      agent_id: dim('agentId'),
      counter_entity_id: dim('counterEntityId'),
      wallet_id: dim('walletId'),
    } as ReportRow;
  });
}

export function feedbackRow(id: string, d: Doc, ctx: ReportContext): ReportRow | null {
  const created = time(d['createdAt']);
  const base = common(created, ctx);
  if (!base) return null;
  const comment = str(d['comment']);
  return {
    feedback_id: id,
    ...base,
    created_at: created,
    kind: str(d['kind']),
    stars: int(d['stars']),
    source: str(d['source']),
    role: str(d['role']),
    tracking_no: str(d['trackingNo']),
    customer_id: str(d['customerId']),
    branch_id: str(d['branchId']),
    sector_id: str(d['sectorId']),
    serving_branch_id: str(d['servingBranchId']),
    route_code: str(d['routeCode']),
    partner_id: str(d['partnerId']),
    complaint: bool(d['complaint']),
    has_comment: comment !== null,
    comment_length: comment === null ? 0 : comment.length,
    has_ticket: str(d['ticketId']) !== null,
    publishable: bool(d['publishable']),
  } as ReportRow;
}

export function ticketRow(id: string, d: Doc, ctx: ReportContext): ReportRow | null {
  const updated = time(d['updatedAt']) ?? time(d['createdAt']);
  const base = common(updated, ctx, updated ?? '');
  if (!base) return null;
  return {
    ticket_id: id,
    ...base,
    created_at: time(d['createdAt']),
    updated_at: updated,
    category: str(d['category']),
    status: str(d['status']),
    source: str(d['source']),
    rating: int(d['rating']),
    tracking_no: str(d['trackingNo']),
    customer_id: str(d['customerId']),
    branch_id: str(d['branchId']),
    sector_id: str(d['sectorId']),
    sla_due_at: time(d['slaDueAt']),
    message_count: countOf(d['messages']),
    resolution_action: str(at(d, 'resolution.action')),
    resolved_at: time(at(d, 'resolution.at')),
    followup_resolved: bool(at(d, 'followUp.resolved')),
  } as ReportRow;
}

export function partnerUpdateRow(id: string, d: Doc, ctx: ReportContext): ReportRow | null {
  const created = time(d['createdAt']) ?? time(d['at']);
  const base = common(created, ctx);
  if (!base) return null;
  return {
    update_id: id,
    ...base,
    at: time(d['at']),
    created_at: created,
    partner_id: str(d['partnerId']),
    handover_id: str(d['handoverId']),
    unit_count: countOf(d['unitIds']),
    status: str(d['status']),
    source: str(d['source']),
    reason: str(d['reason']),
    delivery_verified: bool(d['deliveryVerified']),
    collected_minor: int(d['collectedMinor']),
    collected_currency: str(d['collectedCurrency']),
    refused_count: countOf(d['refused']),
    branch_id: str(d['branchId']),
    sector_id: str(d['sectorId']),
  } as ReportRow;
}

export function fxRateRow(id: string, d: Doc, ctx: ReportContext): ReportRow | null {
  const day = dateOnly(d['date']);
  const base = common(day, ctx);
  if (!base || !day) return null;
  const rate = Number(d['rate']);
  return {
    fx_id: id,
    ...base,
    rate_date: day,
    from_currency: str(d['from']),
    to_currency: str(d['to']),
    rate: Number.isFinite(rate) ? rate : null,
  } as ReportRow;
}

/** The rows of a table for one source document. A document with no usable time gives none. */
export function reportRows(
  table: ReportingTableName,
  id: string,
  d: Doc,
  ctx: ReportContext,
): ReportRow[] {
  switch (table) {
    case 'shipments':
      return one(shipmentRow(id, d, ctx));
    case 'boxes':
      return one(boxRow(id, d, ctx));
    case 'shipment_legs':
      return one(legsRow(id, d, ctx));
    case 'payments':
      return one(paymentRow(id, d, ctx));
    case 'journals':
      return one(journalRow(id, d, ctx));
    case 'journal_lines':
      return journalLineRows(id, d, ctx);
    case 'feedback':
      return one(feedbackRow(id, d, ctx));
    case 'tickets':
      return one(ticketRow(id, d, ctx));
    case 'partner_updates':
      return one(partnerUpdateRow(id, d, ctx));
    case 'fx_rates':
      return one(fxRateRow(id, d, ctx));
  }
}

const one = (row: ReportRow | null): ReportRow[] => (row ? [row] : []);

/** `{table}:{key columns}:{version}`: a retry of one version of one record is the same insert. */
export function insertIdOf(table: ReportingTableName, row: ReportRow): string {
  const t = reportingTable(table);
  const key = t.keyColumns.map((k) => String(row[k])).join(':');
  return `${table}:${key}:${String(row['version_key'])}`;
}

/** Every column of a row is declared, required ones are present, and each value has its column's type. */
export function checkRow(table: ReportingTableName, row: ReportRow): string[] {
  const t = reportingTable(table);
  const problems: string[] = [];
  const known = new Map(t.columns.map((col) => [col.name, col]));
  for (const key of Object.keys(row)) {
    if (!known.has(key)) problems.push(`${table}.${key} is not a declared column`);
  }
  for (const col of t.columns) {
    const v = row[col.name];
    if (v === undefined) {
      problems.push(`${table}.${col.name} is missing`);
      continue;
    }
    if (v === null) {
      if (col.required) problems.push(`${table}.${col.name} is required`);
      continue;
    }
    const ok =
      col.type === 'INTEGER'
        ? Number.isInteger(v)
        : col.type === 'FLOAT'
          ? typeof v === 'number'
          : col.type === 'BOOLEAN'
            ? typeof v === 'boolean'
            : typeof v === 'string';
    if (!ok) problems.push(`${table}.${col.name} should be ${col.type}`);
  }
  return problems;
}
